import { create } from 'zustand';
import { getTenantId } from './authStore';

const ONTOLOGY_API = import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface Domain {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  object_type_ids: string[];
}

export interface DomainLink {
  id: string;
  source: string;   // source domain id
  target: string;   // target domain id
  label?: string | null;
}

// A stable palette so domains without an explicit color still read distinctly.
const DOMAIN_PALETTE = [
  '#0EA5E9', '#7C3AED', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#8B5CF6',
];

export function domainColor(domain: { id: string; color?: string | null }): string {
  if (domain.color) return domain.color;
  let hash = 0;
  for (let i = 0; i < domain.id.length; i++) hash = (hash * 31 + domain.id.charCodeAt(i)) | 0;
  return DOMAIN_PALETTE[Math.abs(hash) % DOMAIN_PALETTE.length];
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface DomainStore {
  domains: Domain[];
  domainLinks: DomainLink[];
  // object_type_id → [domain_id, ...]  (derived from each domain's members)
  domainsByOt: Record<string, string[]>;
  // When set, the graph dims everything outside this domain.
  focusedDomainId: string | null;
  loading: boolean;
  error: string | null;

  fetchDomains: () => Promise<void>;
  setFocusedDomain: (id: string | null) => void;
  createDomain: (name: string, color?: string, objectTypeIds?: string[]) => Promise<void>;
  updateDomain: (id: string, patch: { name?: string; description?: string; color?: string }) => Promise<void>;
  deleteDomain: (id: string) => Promise<void>;
  addTypeToDomain: (domainId: string, objectTypeId: string) => Promise<void>;
  removeTypeFromDomain: (domainId: string, objectTypeId: string) => Promise<void>;
  createDomainLink: (sourceDomainId: string, targetDomainId: string, label?: string) => Promise<void>;
  deleteDomainLink: (linkId: string) => Promise<void>;
}

function invertMembership(domains: Domain[]): Record<string, string[]> {
  const byOt: Record<string, string[]> = {};
  for (const d of domains) {
    for (const otId of d.object_type_ids) {
      (byOt[otId] ||= []).push(d.id);
    }
  }
  return byOt;
}

export const useDomainStore = create<DomainStore>((set, get) => ({
  domains: [],
  domainLinks: [],
  domainsByOt: {},
  focusedDomainId: null,
  loading: false,
  error: null,

  fetchDomains: async () => {
    set({ loading: true, error: null });
    try {
      const resp = await fetch(`${ONTOLOGY_API}/domains`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const data = await resp.json() as { domains: Domain[]; links: DomainLink[] };
      const domains = data.domains ?? [];
      set({
        domains,
        domainLinks: data.links ?? [],
        domainsByOt: invertMembership(domains),
      });
    } catch (e) {
      set({ error: (e as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  setFocusedDomain: (id) => set({ focusedDomainId: id }),

  createDomain: async (name, color, objectTypeIds) => {
    await domainFetch('', { method: 'POST', body: JSON.stringify({ name, color, object_type_ids: objectTypeIds ?? [] }) });
    await get().fetchDomains();
  },
  updateDomain: async (id, patch) => {
    await domainFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
    await get().fetchDomains();
  },
  deleteDomain: async (id) => {
    await domainFetch(`/${id}`, { method: 'DELETE' });
    if (get().focusedDomainId === id) set({ focusedDomainId: null });
    await get().fetchDomains();
  },
  addTypeToDomain: async (domainId, objectTypeId) => {
    await domainFetch(`/${domainId}/members`, { method: 'POST', body: JSON.stringify({ object_type_ids: [objectTypeId] }) });
    await get().fetchDomains();
  },
  removeTypeFromDomain: async (domainId, objectTypeId) => {
    await domainFetch(`/${domainId}/members/${objectTypeId}`, { method: 'DELETE' });
    await get().fetchDomains();
  },
  createDomainLink: async (sourceDomainId, targetDomainId, label) => {
    await domainFetch('/links', { method: 'POST', body: JSON.stringify({ source_domain_id: sourceDomainId, target_domain_id: targetDomainId, label }) });
    await get().fetchDomains();
  },
  deleteDomainLink: async (linkId) => {
    await domainFetch(`/links/${linkId}`, { method: 'DELETE' });
    await get().fetchDomains();
  },
}));

// Shared fetch wrapper for the /domains endpoints — sets tenant + JSON headers
// and throws with the server's body so callers surface real errors.
async function domainFetch(path: string, init: RequestInit): Promise<void> {
  const resp = await fetch(`${ONTOLOGY_API}/domains${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': getTenantId(),
      ...(init.headers || {}),
    },
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    throw new Error(`domains${path} ${resp.status}: ${body.slice(0, 200)}`);
  }
}
